(() => {
  if (customElements.get('pr-image-grid')) return;

  class PrImageGrid extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('.pr-image-grid__items');
      if (!this.track) return;

      this.progress = this.querySelector('[data-pr-image-grid-progress]');
      this.progressThumb = this.querySelector('[data-pr-image-grid-progress-thumb]');
      this.progressTimer = null;
      this.progressFrame = null;

      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.queueProgressUpdate.bind(this);
      this.track.addEventListener('keydown', this.onKeyDown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.progress && this.progressThumb) {
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.onResize);
        }
        this.queueProgressUpdate();
      }

      this.initialized = true;
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.track?.removeEventListener('keydown', this.onKeyDown);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.progressTimer);
      window.cancelAnimationFrame(this.progressFrame);
      this.initialized = false;
    }

    handleScroll() {
      if (
        this.dataset.mobileLayout !== 'slider' ||
        !window.matchMedia('(max-width: 768px)').matches ||
        !this.progress
      ) {
        return;
      }

      this.queueProgressUpdate();
      this.progress.classList.add('is-active');
      window.clearTimeout(this.progressTimer);
      this.progressTimer = window.setTimeout(() => {
        this.progress.classList.remove('is-active');
      }, 800);
    }

    queueProgressUpdate() {
      if (!this.progress || !this.progressThumb || this.progressFrame) return;

      this.progressFrame = window.requestAnimationFrame(() => {
        this.progressFrame = null;
        this.updateProgress();
      });
    }

    updateProgress() {
      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const isOverflowing = maximumScroll > 1;
      this.progress.classList.toggle('is-overflowing', isOverflowing);

      if (!isOverflowing) {
        this.progress.classList.remove('is-active');
        return;
      }

      const visibleRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
      const scrollProgress = Math.min(1, Math.max(0, this.track.scrollLeft / maximumScroll));
      const thumbWidth = visibleRatio * 100;
      const thumbOffset = scrollProgress * (100 - thumbWidth);

      this.progress.style.setProperty('--pr-image-grid-progress-width', `${thumbWidth}%`);
      this.progress.style.setProperty('--pr-image-grid-progress-offset', `${thumbOffset}%`);
    }

    handleKeyDown(event) {
      if (
        this.dataset.mobileLayout !== 'slider' ||
        !window.matchMedia('(max-width: 768px)').matches ||
        event.target !== this.track ||
        !['ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        return;
      }

      const firstCard = this.track.querySelector('.pr-image-grid__card');
      if (!firstCard) return;

      event.preventDefault();
      const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollBy({
        left: direction * (firstCard.getBoundingClientRect().width + gap),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    handleBlockSelect(event) {
      if (
        this.dataset.mobileLayout !== 'slider' ||
        !window.matchMedia('(max-width: 768px)').matches
      ) {
        return;
      }

      const card = event.target.closest('.pr-image-grid__card');
      if (!card || !this.contains(card)) return;

      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }

  customElements.define('pr-image-grid', PrImageGrid);
})();
